import { useGlobalStore } from '@old/global-store/use-store';
import {
  SilobagListField,
  SilobagListFilter,
  SilobagListServiceContract,
  SilobagListSort,
} from '@siloreal:shared:api/service/silobag/silobag-list';
import { ExplorerServiceAdapterState } from '@siloreal:shared:ui/collection/explorer';

export type SilobagListAdapterConfig = {
  implicitFilters?: SilobagListFilter;
};

type ActiveDrawerFilters = ExplorerServiceAdapterState['activeDrawerFilters'];
type SortingModel = ExplorerServiceAdapterState['sortingModel'];
type DraftFilter = Partial<Record<SilobagListField, unknown>>;

type DateRangeFilterValue = {
  startDate?: unknown;
  endDate?: unknown;
};

type InputRangeFilterValue = {
  min?: unknown;
  max?: unknown;
};

const SILOBAG_LIST_FIELDS = new Set<SilobagListField>([
  'name',
  'id',
  'silobag_species_type_id',
  'silobag_product_type_id',
  'created_at',
  'silobag_harvest_id',
  'bagging_date',
  'initial_humidity',
  'remaining_stock',
  'total_extracted_weight',
  'organization_id',
  'company_id',
  'countryside_id',
  'lot_id',
  'countryside_zone_id',
  'color_id',
  'sat_view_active',
  'is_silo_metrix_active',
  'active',
  'corvus',
  'vrr',
  'silobag_data_status_id',
  'inconsistencies',
  'tag_id',
]);

type State = ExplorerServiceAdapterState;
type Config = SilobagListAdapterConfig;
export function toSilobagListContract(state: State, config: Config = {}): SilobagListServiceContract {
  const { organizationId: organization_id } = useGlobalStore.getState();
  return {
    pagination: {
      page: state.paginationModel.page,
      take: state.paginationModel.pageSize,
    },
    sort: toSilobagListSort(state.sortingModel),
    search: state.liveSearch,
    filter: {
      ...toSilobagListFilter(state.activeDrawerFilters),
      ...config.implicitFilters,
      organization_id,
    },
  };
}

function toSilobagListFilter(activeDrawerFilters: ActiveDrawerFilters): SilobagListFilter {
  const filter: DraftFilter = {};

  Object.entries(activeDrawerFilters).forEach(([field, value]) => {
    if (!isSilobagListField(field)) return;
    if (!hasFilterValue(value)) return;

    filter[field] = toFilterValue(value);
  });

  return filter as SilobagListFilter;
}

function toSilobagListSort(sortingModel: SortingModel): SilobagListSort {
  return sortingModel.flatMap((sortItem) => {
    if (!sortItem.sort || !isSilobagListField(sortItem.field)) return [];

    return [
      {
        field: sortItem.field,
        direction: sortItem.sort,
      },
    ];
  });
}

function isSilobagListField(field: string): field is SilobagListField {
  return SILOBAG_LIST_FIELDS.has(field as SilobagListField);
}

function isDateRangeFilterValue(value: unknown): value is DateRangeFilterValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  return 'startDate' in value || 'endDate' in value;
}

function isInputRangeFilterValue(value: unknown): value is InputRangeFilterValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  return 'min' in value || 'max' in value;
}

function hasFilterValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasFilterValue);
  if (isDateRangeFilterValue(value)) return hasFilterValue(value.startDate) || hasFilterValue(value.endDate);
  if (isInputRangeFilterValue(value)) return hasFilterValue(value.min) || hasFilterValue(value.max);

  return true;
}

function toFilterValue(value: unknown): unknown {
  if (isDateRangeFilterValue(value)) {
    return {
      start: toFilterValue(value.startDate),
      end: toFilterValue(value.endDate),
    };
  }

  if (isInputRangeFilterValue(value)) {
    return {
      start: toFilterValue(value.min),
      end: toFilterValue(value.max),
    };
  }

  if (Array.isArray(value)) {
    const values = value.map(toFilterValue).filter(hasFilterValue);

    return values.length === 1 ? values[0] : values;
  }

  if (typeof value !== 'string') return value;

  return value.trim();
}
